/**
 * BalanceDashboard.tsx — BETA 21.62 dev balance editor (Phase 1).
 *
 * Route: /balance . Lists every item / perk / shrine with its sprite + every editable
 * number. Edit a number → it mutates the live game data AND saves to Supabase
 * (balance_overrides) so it's live for everyone with no redeploy. Claude reads that
 * same row to bake the values into code when finalized. Weapons + enemies + analytics
 * graphs land in later phases.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'wouter';
import { buildBalanceData, getDefault, initBalanceData, helpForField, regenItemById, type EditField } from '../game/balanceData';
import { getBullshiiiiById } from '../game/bunchaBullshiiii';
import {
  loadBalanceOverrides,
  setOverrideLocal, clearOverrideLocal, saveBalanceOverridesDebounced,
  overrideCount, exportOverridesJson, isBalanceLoaded,
} from '../game/balanceOverrides';
import { BALANCE_SOUND_MAP, playBalanceSound } from '../game/balanceSounds';

const RARITY_COLOR: Record<string, string> = {
  common: '#39ff14', uncommon: '#39ff14', rare: '#b15cff', epic: '#b15cff',
  legendary: '#ffd700', negative: '#ff3333', perk: '#5ad1ff',
};

export default function BalanceDashboard() {
  const [tab, setTab] = useState('items');
  const [, setTick] = useState(0);
  const [ready, setReady] = useState(isBalanceLoaded());
  // BETA 21.71 — which field's help bubble is open ('catKey:entId:fieldKey') or null.
  // The native title= tooltip only shows on desktop hover; on the ROG Ally (touch/
  // controller) Dylan saw the help cursor but no text. Tap a label to toggle a bubble.
  const [helpOpen, setHelpOpen] = useState<string | null>(null);
  const rerender = () => setTick(x => x + 1);

  // Ensure overrides are loaded + applied before showing (idempotent).
  useEffect(() => {
    let on = true;
    (async () => {
      if (!isBalanceLoaded()) await loadBalanceOverrides();
      initBalanceData();
      if (on) { setReady(true); rerender(); }
    })();
    return () => { on = false; };
  }, []);

  // Built once; the get/set closures read/write the live game objects.
  const data = useMemo(() => buildBalanceData(), [ready]);
  const cat = data.find(c => c.key === tab) ?? data[0];

  const onEdit = (catKey: string, entId: string, f: EditField, raw: string) => {
    let v: number | string;
    if (f.kind === 'rarity') { v = raw; }
    else { const n = parseFloat(raw); if (!Number.isFinite(n)) return; v = n; }
    f.set(v);
    if (catKey === 'items') regenItemById(entId);   // BETA 21.75 — repaint the card text from the new number
    const def = getDefault(catKey, entId, f.key);
    if (def !== undefined && def === v) clearOverrideLocal(catKey, entId, f.key);
    else setOverrideLocal(catKey, entId, f.key, v);
    saveBalanceOverridesDebounced('dashboard');
    rerender();
  };
  const onReset = (catKey: string, entId: string, f: EditField) => {
    const def = getDefault(catKey, entId, f.key);
    if (def === undefined) return;
    f.set(def);
    if (catKey === 'items') regenItemById(entId);   // BETA 21.75 — repaint card text on reset too
    clearOverrideLocal(catKey, entId, f.key);
    saveBalanceOverridesDebounced('dashboard');
    rerender();
  };
  const copyExport = () => {
    try { void navigator.clipboard?.writeText(exportOverridesJson()); } catch { /* no clipboard */ }
  };

  if (!ready) {
    return <div style={{ ...styles.page, justifyContent: 'center', alignItems: 'center' }}>Loading balance data…</div>;
  }

  return (
    <div style={styles.page} onClick={() => { if (helpOpen) setHelpOpen(null); }}>
      <header style={styles.header}>
        <Link href="/" style={styles.back}>← Menu</Link>
        <h1 style={styles.title}>⚖️ Balance Dashboard</h1>
        <div style={styles.headerRight}>
          <span style={styles.changes}>{overrideCount()} changes</span>
          <button style={styles.exportBtn} onClick={copyExport}>Copy changes JSON</button>
        </div>
      </header>

      <div style={styles.hint}>Click any number, type a new one, press Enter — it saves automatically and goes live. Changed values turn yellow (hit ↺ to reset). Tap or hover a label (dotted underline) for what it means + its units.</div>

      <nav style={styles.tabs}>
        {data.map(c => (
          <button key={c.key} onClick={() => setTab(c.key)}
            style={{ ...styles.tab, ...(tab === c.key ? styles.tabActive : {}) }}>
            {c.label} <span style={styles.tabCount}>{c.entities.length}</span>
          </button>
        ))}
      </nav>

      <div style={styles.grid}>
        {cat.entities.map(ent => {
          const color = RARITY_COLOR[ent.rarity] ?? '#888';
          return (
            <div key={ent.id} style={{ ...styles.card, borderColor: color }}>
              <div style={styles.cardHead}>
                {ent.sprite
                  ? <img src={ent.sprite} alt="" style={styles.sprite} onError={e => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
                  : ent.emoji
                    ? <div style={{ ...styles.sprite, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, background: '#0d1017', borderWidth: 1, borderStyle: 'solid', borderColor: color }}>{ent.emoji}</div>
                    : <div style={{ ...styles.sprite, background: color, opacity: 0.25 }} />}
                <div style={styles.cardTitle}>
                  <div style={styles.cardName}>{ent.name}</div>
                  {(() => {
                    // BETA 21.75 — for items, read the LIVE regenerated description so it
                    // updates the instant a tuning value is edited (ent.desc is a useMemo snapshot).
                    const liveDesc = cat.key === 'items' ? (getBullshiiiiById(ent.id)?.description ?? ent.desc) : ent.desc;
                    return liveDesc ? <div style={styles.cardDesc}>{liveDesc}</div> : null;
                  })()}
                </div>
              </div>
              {BALANCE_SOUND_MAP[ent.id] && (
                <div style={styles.soundRow}>
                  <span style={styles.soundLabel}>🔊</span>
                  {BALANCE_SOUND_MAP[ent.id].map(s => (
                    <button
                      key={s.file}
                      onClick={() => playBalanceSound(s.file)}
                      style={styles.soundBtn}
                      title={`Play "${s.label}" once`}
                    >▶ {s.label}</button>
                  ))}
                </div>
              )}
              {ent.fields.map(f => {
                const def = getDefault(cat.key, ent.id, f.key);
                const cur = f.get();
                const changed = def !== undefined && def !== cur;
                return (
                  <div key={f.key} style={styles.fieldRow}>
                    {(() => {
                      const helpId = `${cat.key}:${ent.id}:${f.key}`;
                      const isOpen = helpOpen === helpId;
                      return (
                        <span style={styles.fieldLabelWrap}>
                          <span
                            role="button"
                            tabIndex={0}
                            style={styles.fieldLabel}
                            title={helpForField(f)}
                            onClick={e => { e.stopPropagation(); setHelpOpen(isOpen ? null : helpId); }}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHelpOpen(isOpen ? null : helpId); } }}
                          >{f.label}</span>
                          {isOpen && (
                            <span style={styles.helpBubble} onClick={e => e.stopPropagation()}>{helpForField(f)}</span>
                          )}
                        </span>
                      );
                    })()}
                    {f.kind === 'rarity'
                      ? <select value={String(cur)} onChange={e => onEdit(cat.key, ent.id, f, e.target.value)}
                          style={{ ...styles.input, width: 110, ...(changed ? styles.inputChanged : {}) }}>
                          {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      : <input type="number" step="any" defaultValue={cur} key={`${f.key}:${cur}`}
                          onBlur={e => onEdit(cat.key, ent.id, f, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                          style={{ ...styles.input, ...(changed ? styles.inputChanged : {}) }} />}
                    <span style={styles.unit}>{f.unit}</span>
                    {changed
                      ? <button title={`reset to ${def}`} onClick={() => onReset(cat.key, ent.id, f)} style={styles.resetBtn}>↺</button>
                      : <span style={styles.resetSpacer} />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  // BETA 21.66 — position:fixed + inset:0 makes the dashboard a viewport-anchored OWN
  // scroll container, bulletproof against the parent layout (the game locks body overflow
  // for the fullscreen canvas, and #root has no fixed height, so plain height:100vh wasn't
  // enough). overflowY:auto + touch scrolling so it scrolls on the handheld too.
  page: { position: 'fixed', inset: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0d0f14', color: '#e8e8e8', fontFamily: '"Share Tech Mono", monospace', padding: '16px 20px 60px', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 6 },
  back: { color: '#5ad1ff', textDecoration: 'none', fontSize: 14 },
  title: { fontSize: 22, margin: 0, flex: 1 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },
  changes: { fontSize: 13, color: '#ffd700' },
  exportBtn: { background: '#1c2230', color: '#5ad1ff', border: '1px solid #2c3650', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 },
  hint: { fontSize: 12, color: '#8a93a6', marginBottom: 12 },
  tabs: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { background: '#161a22', color: '#aeb6c4', borderWidth: 1, borderStyle: 'solid', borderColor: '#262d3a', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 },
  tabActive: { background: '#222b3d', color: '#fff', borderColor: '#3a4668' },
  tabCount: { opacity: 0.6, fontSize: 11, marginLeft: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 },
  // BETA 21.79 — Dylan: "if a card has a lot of stuff, let me scroll inside the card."
  // Cap the height + scroll internally so a field-heavy item (Whoopie etc.) doesn't
  // run off-screen and you can reach every number.
  card: { background: '#141821', borderWidth: 1, borderStyle: 'solid', borderRadius: 10, padding: 12, maxHeight: 460, overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  sprite: { width: 40, height: 40, objectFit: 'contain', borderRadius: 6, flexShrink: 0 },
  cardTitle: { minWidth: 0 },
  cardName: { fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardMeta: { fontSize: 11, opacity: 0.85 },
  cardDesc: { fontSize: 10.5, color: '#9aa3b2', lineHeight: 1.25, marginTop: 2 },
  soundRow: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, padding: '5px 0 2px', borderTop: '1px solid #1a2030', marginTop: 4 },
  soundLabel: { fontSize: 12, opacity: 0.8, marginRight: 2 },
  soundBtn: { background: '#15233a', border: '1px solid #2f7fd0', color: '#bfe0ff', cursor: 'pointer', fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '3px 8px' },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' },
  // BETA 21.71 — wrapper is the positioning context for the absolutely-positioned help bubble.
  fieldLabelWrap: { flex: 1, minWidth: 0, position: 'relative', display: 'flex' },
  fieldLabel: { flex: 1, fontSize: 12, color: '#c2c8d4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'help', textDecoration: 'underline dotted', textDecorationColor: '#3a4150', textUnderlineOffset: 3 },
  // BETA 21.71 — tap-to-show help popover (works on the Ally's touch/controller, unlike native title=).
  helpBubble: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, maxWidth: 280, width: 'max-content', whiteSpace: 'normal', background: '#1c2230', color: '#dfe5f0', borderWidth: 1, borderStyle: 'solid', borderColor: '#3a4668', borderRadius: 6, padding: '7px 9px', fontSize: 11, lineHeight: 1.35, boxShadow: '0 6px 18px rgba(0,0,0,0.55)', pointerEvents: 'auto' },
  input: { width: 74, background: '#0d1017', color: '#e8e8e8', borderWidth: 1, borderStyle: 'solid', borderColor: '#2a3242', borderRadius: 5, padding: '4px 6px', fontFamily: 'inherit', fontSize: 13, textAlign: 'right' },
  inputChanged: { borderColor: '#ffd700', color: '#ffd700', background: '#1a1605' },
  unit: { fontSize: 11, color: '#7a8294', width: 28 },
  resetBtn: { background: 'none', border: 'none', color: '#ffd700', cursor: 'pointer', fontSize: 14, padding: 0, width: 18 },
  resetSpacer: { width: 18 },
};
